import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaCheck } from 'react-icons/fa';

// Themed drop-in replacement for a native select element. The browser-drawn option
// list can't be styled (blue highlight), so the list here is app-rendered: a
// portaled, viewport-clamped panel using the app's cloudza palette.
//
// Call sites keep their native JSX shape - <option> children are parsed via
// React.Children, and selection emits onChange({ target: { value } }) so
// existing `e.target.value` handlers work unchanged. Values are compared and
// emitted as strings, matching native select semantics.
//
// portal={false} renders the list absolutely inside the component wrapper
// instead of on document.body - needed when the select lives inside another
// portaled popup (e.g. DateTimeInput) whose outside-click logic would
// otherwise treat clicks on the portaled list as "outside".

const textOf = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement(node)) return textOf(node.props.children);
  return '';
};

const extractOptions = (children) => {
  const options = [];
  const walk = (nodes) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;
      if (child.type === 'option') {
        const label = textOf(child.props.children);
        options.push({
          value:
            child.props.value !== undefined ? String(child.props.value) : label,
          label,
          disabled: Boolean(child.props.disabled),
        });
      } else if (child.props && child.props.children) {
        // Fragments or wrapper elements: recurse to find nested options.
        walk(child.props.children);
      }
    });
  };
  walk(children);
  return options;
};

const MAX_LIST_HEIGHT = 264; // max-h-64 + py-1

let listIdCounter = 0;

const SelectField = ({
  value,
  onChange,
  disabled = false,
  className,
  placeholder,
  portal = true,
  multiple = false,
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 0 });
  const anchorRef = useRef(null);
  const listRef = useRef(null);
  const typeaheadRef = useRef({ buffer: '', at: 0 });
  const listIdRef = useRef(null);
  if (listIdRef.current === null) {
    listIdRef.current = id
      ? `${id}-listbox`
      : `select-field-listbox-${++listIdCounter}`;
  }

  const options = extractOptions(children);
  const selectedValues = multiple
    ? (Array.isArray(value) ? value : []).map(String)
    : [];
  const strValue = value === undefined || value === null ? '' : String(value);
  const selectedIndex = multiple
    ? -1
    : options.findIndex((o) => o.value === strValue);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const isOptSelected = (opt, i) =>
    multiple ? selectedValues.includes(opt.value) : i === selectedIndex;

  const triggerLabel = multiple
    ? options
        .filter((o) => selectedValues.includes(o.value))
        .map((o) => o.label)
        .join(', ') ||
      (placeholder !== undefined ? placeholder : 'Select...')
    : selected
    ? selected.label
    : placeholder !== undefined
    ? placeholder
    : strValue !== ''
    ? strValue
    : options[0]
    ? options[0].label
    : '';

  const emit = useCallback(
    (v) => {
      if (onChange) onChange({ target: { value: v } });
    },
    [onChange]
  );

  // ---- popup positioning (portal + fixed + viewport clamp, same approach
  // as DateTimeInput so ancestor overflow/z-index can't clip the list) ------
  const updatePopupPosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const height = listRef.current
      ? listRef.current.offsetHeight
      : MAX_LIST_HEIGHT;
    const width = rect.width;
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - width - 8)
    );
    let top = rect.bottom + 4;
    if (top + height > window.innerHeight - 8) {
      const above = rect.top - height - 4;
      if (above >= 8) top = above; // flip above when no room below
      else top = Math.max(8, window.innerHeight - height - 8);
    }
    setPopupPos({ top, left, width });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (portal) updatePopupPosition();

    const handleClickOutside = (event) => {
      if (
        anchorRef.current && !anchorRef.current.contains(event.target) &&
        listRef.current && !listRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    // Capture-phase Escape with stopPropagation so an enclosing popup
    // (e.g. DateTimeInput's panel) doesn't also close on the same press.
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, true);
    if (portal) {
      window.addEventListener('scroll', updatePopupPosition, true);
      window.addEventListener('resize', updatePopupPosition);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (portal) {
        window.removeEventListener('scroll', updatePopupPosition, true);
        window.removeEventListener('resize', updatePopupPosition);
      }
    };
  }, [isOpen, portal, updatePopupPosition]);

  // Keep the active option visible while navigating with the keyboard.
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const el = listRef.current.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [isOpen, activeIndex]);

  const firstEnabled = (from, step) => {
    for (let i = from; i >= 0 && i < options.length; i += step) {
      if (!options[i].disabled) return i;
    }
    return -1;
  };

  const openList = () => {
    if (disabled || options.length === 0) return;
    setActiveIndex(
      selectedIndex >= 0 && !options[selectedIndex].disabled
        ? selectedIndex
        : firstEnabled(0, 1)
    );
    setIsOpen(true);
  };

  const toggleList = () => {
    if (isOpen) setIsOpen(false);
    else openList();
  };

  const selectOption = (index) => {
    const opt = options[index];
    if (!opt || opt.disabled) return;
    if (multiple) {
      // Toggle; stay open, and mimic the native multi-select event shape so
      // handlers reading e.target.selectedOptions keep working.
      const next = selectedValues.includes(opt.value)
        ? selectedValues.filter((v) => v !== opt.value)
        : [...selectedValues, opt.value];
      if (onChange) {
        onChange({
          target: {
            value: next[0] !== undefined ? next[0] : '',
            selectedOptions: next.map((v) => ({ value: v })),
          },
        });
      }
      return;
    }
    emit(opt.value);
    setIsOpen(false);
  };

  const moveActive = (step) => {
    setActiveIndex((current) => {
      const start = current < 0 ? (step > 0 ? 0 : options.length - 1) : current + step;
      const next = firstEnabled(start, step);
      return next >= 0 ? next : current;
    });
  };

  const handleTypeahead = (key) => {
    const now = Date.now();
    const state = typeaheadRef.current;
    state.buffer = now - state.at > 700 ? key : state.buffer + key;
    state.at = now;
    const query = state.buffer.toLowerCase();
    const match = options.findIndex(
      (o) => !o.disabled && o.label.toLowerCase().startsWith(query)
    );
    if (match >= 0) {
      if (isOpen) setActiveIndex(match);
      else emit(options[match].value);
    }
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openList();
      } else if (e.key.length === 1 && e.key !== ' ') {
        handleTypeahead(e.key);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(firstEnabled(0, 1));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(firstEnabled(options.length - 1, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectOption(activeIndex);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        if (e.key.length === 1) handleTypeahead(e.key);
        break;
    }
  };

  const triggerClassName = className || 'select-field w-full';
  // Mirror layout-affecting utilities onto the wrapper so the field
  // participates in flex/grid layouts as the bare native select did.
  const wrapperLayout = ['w-full', 'flex-1', 'min-w-0']
    .filter((c) => triggerClassName.split(/\s+/).includes(c))
    .join(' ');

  const renderList = () => (
    <ul
      ref={listRef}
      id={listIdRef.current}
      role="listbox"
      aria-label={ariaLabel}
      className={`${
        portal ? 'fixed' : 'absolute left-0 top-full mt-1 w-full'
      } popup-surface z-[110] max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1 custom-scrollbar`}
      style={
        portal
          ? { top: popupPos.top, left: popupPos.left, width: popupPos.width }
          : undefined
      }
    >
      {options.map((opt, i) => {
        const isSelected = isOptSelected(opt, i);
        const isActive = i === activeIndex;
        let cls = 'px-3.5 py-2 text-sm flex items-center justify-between gap-2';
        if (opt.disabled) {
          cls += ' text-gray-300 cursor-not-allowed';
        } else if (isSelected) {
          cls += ' bg-cloudza-50 text-cloudza-700 font-semibold cursor-pointer';
        } else if (isActive) {
          cls += ' bg-gray-50 text-gray-700 cursor-pointer';
        } else {
          cls += ' text-gray-700 hover:bg-gray-50 cursor-pointer';
        }
        return (
          <li
            key={`${opt.value}-${i}`}
            role="option"
            aria-selected={isSelected}
            aria-disabled={opt.disabled || undefined}
            data-active={isActive || undefined}
            className={cls}
            onMouseEnter={() => !opt.disabled && setActiveIndex(i)}
            onClick={() => selectOption(i)}
          >
            <span className="truncate">{opt.label || ' '}</span>
            {isSelected && (
              <FaCheck className="w-3 h-3 flex-shrink-0 text-cloudza-600" />
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={`relative ${wrapperLayout}`} ref={anchorRef}>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listIdRef.current}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        data-testid={dataTestId}
        disabled={disabled}
        onClick={toggleList}
        onKeyDown={handleTriggerKeyDown}
        className={`${triggerClassName} text-left truncate disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {triggerLabel || ' '}
      </button>
      {isOpen &&
        (portal ? createPortal(renderList(), document.body) : renderList())}
    </div>
  );
};

export default SelectField;
